import * as nifti from 'nifti-reader-js';

/**
 * Decompress and parse a NIfTI volume from a raw ArrayBuffer.
 *
 * Pure and worker-safe: no DOM access, no app imports, so it runs unchanged on
 * the main thread (fallback) or inside a Web Worker. The gzip inflate of a CT
 * volume (~60 MB compressed to ~134 MB) is the expensive part and the reason
 * this is worth moving off the main thread.
 *
 * Returns { header, image } where `image` is the raw image ArrayBuffer (a fresh
 * buffer produced by nifti.readImage, so it is safe to transfer), or null if
 * the buffer is not a NIfTI volume.
 */
export function parseVolume(buffer) {
  if (!buffer) return null;

  let data = buffer;
  if (nifti.isCompressed(data)) {
    data = nifti.decompress(data);
  }

  if (!nifti.isNIFTI(data)) return null;

  const rawHeader = nifti.readHeader(data);
  const image = nifti.readImage(rawHeader, data);

  // postMessage's structured clone drops the header's prototype and methods, so
  // hand back a plain object carrying only its data properties. That covers
  // everything the app reads (dims, datatypeCode, scl_slope, scl_inter) and any
  // other scalar/array field, without relying on the class surviving transit.
  const header = {};
  for (const key of Object.keys(rawHeader)) {
    if (typeof rawHeader[key] !== 'function') {
      header[key] = rawHeader[key];
    }
  }

  return { header, image };
}

/**
 * Wrap a raw NIfTI image buffer in the typed array its datatype code implies.
 * Kept alongside the parser so the main thread and any future caller agree on
 * the mapping. The wrap is a view over the buffer, not a copy.
 */
export function imageToTypedArray(image, datatypeCode) {
  switch (datatypeCode) {
    case 2:  // UINT8
      return new Uint8Array(image);
    case 4:  // INT16
      return new Int16Array(image);
    case 16: // FLOAT32
      return new Float32Array(image);
    default:
      console.warn('Unsupported NIfTI datatype:', datatypeCode, '- falling back to Int16');
      return new Int16Array(image);
  }
}
